import React, { useState, useEffect, useCallback } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import { COLORS } from '../theme';

const STATES = [
  { code: 'AL', name: 'Alabama' },      { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },      { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },   { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },  { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' },      { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },       { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },     { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },         { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },     { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },        { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },{ code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },    { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },     { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },     { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },{ code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },   { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },{ code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },         { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },       { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' }, { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' }, { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },        { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },      { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },   { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },    { code: 'WY', name: 'Wyoming' },
];

const COL = '2fr 70px 110px 170px 60px';

const headerCell = {
  fontSize: '12px',
  color: COLORS.textSecondary,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  padding: '0 0 12px 0',
  fontWeight: '600',
};

const cell = {
  padding: '12px 0',
  fontSize: '13px',
  color: COLORS.textPrimary,
};

function Toggle({ checked, onChange, disabled }) {
  return (
    <div
      role="switch"
      aria-checked={checked}
      aria-disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      style={{
        width: '44px',
        height: '24px',
        borderRadius: '12px',
        backgroundColor: checked ? COLORS.accent : COLORS.border,
        position: 'relative',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'background-color 0.2s',
        flexShrink: 0,
        opacity: disabled ? 0.5 : 1,
      }}
      title={disabled ? 'Read-only access' : undefined}
    >
      <div style={{
        position: 'absolute',
        top: '2px',
        left: checked ? '22px' : '2px',
        width: '20px',
        height: '20px',
        borderRadius: '50%',
        backgroundColor: '#fff',
        transition: 'left 0.2s',
        boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
      }} />
    </div>
  );
}

function slugify(name) {
  return name.trim().toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
}

function formatDate(ts) {
  if (!ts) return '—';
  try {
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return '—';
  }
}

const INPUT_STYLE = {
  color: COLORS.textPrimary,
  backgroundColor: COLORS.surfaceHover,
  border: `1px solid ${COLORS.border}`,
  padding: '8px 12px',
  borderRadius: '6px',
  fontSize: '13px',
  outline: 'none',
  fontFamily: 'inherit',
};

/**
 * T122 (2) (Prompt #569): every cities/{id} write goes through the superadmin,
 * audited setCityEnabled / addCity callables (firestore.rules deny client
 * writes for everyone). Only a superadmin sees write controls; an operator is
 * read-only (the callables refuse operators too).
 */
export default function CitiesPage() {
  const { claims } = useAuth();
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [addName, setAddName] = useState('');
  const [addState, setAddState] = useState('');
  const [addError, setAddError] = useState('');
  const [addLoading, setAddLoading] = useState(false);
  const [toggleError, setToggleError] = useState('');

  const canWrite = claims.accessLevel === 'superadmin';

  const loadCities = useCallback(async () => {
    const snap = await getDocs(collection(db, 'cities'));
    const list = snap.docs.map(d => ({ cityId: d.id, ...d.data() }));
    list.sort((a, b) => {
      if (a.enabled !== b.enabled) return a.enabled ? -1 : 1;
      const sc = (a.state || '').localeCompare(b.state || '');
      if (sc !== 0) return sc;
      return (a.name || '').localeCompare(b.name || '');
    });
    setCities(list);
    setLoading(false);
  }, []);

  useEffect(() => { loadCities(); }, [loadCities]);

  async function handleToggle(city) {
    if (!canWrite || toggling) return;
    setToggling(city.cityId);
    setToggleError('');
    try {
      await httpsCallable(functions, 'setCityEnabled')({ cityId: city.cityId, enabled: !city.enabled });
      await loadCities();
    } catch (err) {
      setToggleError(err?.code === 'functions/permission-denied'
        ? 'Only a superadmin can change cities.'
        : `Failed to update ${city.name}. Please try again.`);
    } finally {
      setToggling(null);
    }
  }

  async function handleAddCity(e) {
    e.preventDefault();
    setAddError('');
    if (!addName.trim()) { setAddError('City name is required.'); return; }
    if (!addState) { setAddError('State is required.'); return; }

    const cityId = `${slugify(addName)}-${addState.toLowerCase()}`;
    if (cities.find(c => c.cityId === cityId)) {
      setAddError(`${addName.trim()}, ${addState} already exists.`);
      return;
    }

    setAddLoading(true);
    try {
      // The callable builds the same id and doc (disabled), refuses an
      // existing id, and writes an admin_actions audit entry.
      await httpsCallable(functions, 'addCity')({ name: addName.trim(), state: addState });
      setAddName('');
      setAddState('');
      setShowAddForm(false);
      await loadCities();
    } catch (err) {
      if (err?.code === 'functions/already-exists') setAddError(`${addName.trim()}, ${addState} already exists.`);
      else if (err?.code === 'functions/invalid-argument') setAddError('Use letters, spaces, . \' or - in the city name.');
      else if (err?.code === 'functions/permission-denied') setAddError('Only a superadmin can add cities.');
      else setAddError('Failed to add city. Please try again.');
    } finally {
      setAddLoading(false);
    }
  }

  return (
    <>
      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
        <div style={{ fontSize: '13px', color: COLORS.textSecondary }}>
          {loading ? '' : `${cities.length} cit${cities.length === 1 ? 'y' : 'ies'} · ${cities.filter(c => c.enabled).length} enabled`}
          {!canWrite && (
            <span style={{ marginLeft: '12px', color: COLORS.warning, fontSize: '12px' }}>
              ⚠ Read-only access
            </span>
          )}
        </div>
        {canWrite && (
          <button
            onClick={() => { setShowAddForm(v => !v); setAddError(''); }}
            style={{
              backgroundColor: showAddForm ? COLORS.surfaceHover : COLORS.accent,
              color: showAddForm ? COLORS.textSecondary : '#1B1B1F',
              border: showAddForm ? `1px solid ${COLORS.border}` : 'none',
              padding: '8px 16px',
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
            }}
          >
            {showAddForm ? 'Cancel' : '+ Add City'}
          </button>
        )}
      </div>

      {/* Inline Add Form */}
      {showAddForm && (
        <div style={{
          backgroundColor: COLORS.surface,
          border: `1px solid ${COLORS.border}`,
          borderRadius: '8px',
          padding: '20px 24px',
          marginBottom: '20px',
        }}>
          <div style={{ fontSize: '13px', fontWeight: '600', color: COLORS.textPrimary, marginBottom: '16px' }}>
            Add New City
          </div>
          <form onSubmit={handleAddCity} style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: '1 1 200px' }}>
              <label style={{ fontSize: '12px', color: COLORS.textSecondary }}>City Name</label>
              <input
                type="text"
                value={addName}
                onChange={e => setAddName(e.target.value)}
                placeholder="e.g. Portland"
                style={{ ...INPUT_STYLE, width: '100%', boxSizing: 'border-box' }}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: '0 0 160px' }}>
              <label style={{ fontSize: '12px', color: COLORS.textSecondary }}>State</label>
              <select
                value={addState}
                onChange={e => setAddState(e.target.value)}
                style={{ ...INPUT_STYLE, width: '100%', boxSizing: 'border-box' }}
              >
                <option value="">Select state…</option>
                {STATES.map(s => (
                  <option key={s.code} value={s.code}>{s.name} ({s.code})</option>
                ))}
              </select>
            </div>
            <button
              type="submit"
              disabled={addLoading}
              style={{
                backgroundColor: COLORS.accent,
                color: '#1B1B1F',
                border: 'none',
                padding: '8px 20px',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: '600',
                cursor: addLoading ? 'not-allowed' : 'pointer',
                opacity: addLoading ? 0.6 : 1,
                height: '36px',
              }}
            >
              {addLoading ? 'Adding…' : 'Add City'}
            </button>
          </form>
          {addError && (
            <div style={{ marginTop: '12px', fontSize: '13px', color: COLORS.danger }}>{addError}</div>
          )}
          {addName.trim() && addState && (
            <div style={{ marginTop: '10px', fontSize: '12px', color: COLORS.textMuted }}>
              Doc ID: {slugify(addName)}-{addState.toLowerCase()}
            </div>
          )}
        </div>
      )}

      {toggleError && (
        <div role="alert" style={{ marginBottom: '12px', fontSize: '13px', color: COLORS.danger }}>{toggleError}</div>
      )}

      {/* Table */}
      <div style={{ backgroundColor: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: '8px', overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ display: 'grid', gridTemplateColumns: COL, padding: '0 20px', borderBottom: `1px solid ${COLORS.border}` }}>
          {['City', 'State', 'Status', 'Enabled Date', 'Toggle'].map(h => (
            <div key={h} style={headerCell}>{h}</div>
          ))}
        </div>

        {loading ? (
          Array.from({ length: 5 }).map((_, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: COL, padding: '14px 20px', borderBottom: `1px solid ${COLORS.border}30` }}>
              {Array.from({ length: 5 }).map((__, j) => (
                <div key={j} style={{ height: '14px', backgroundColor: COLORS.surfaceHover, borderRadius: '4px', width: '70%' }} />
              ))}
            </div>
          ))
        ) : cities.length === 0 ? (
          <div style={{ padding: '32px 20px', textAlign: 'center', color: COLORS.textMuted, fontSize: '13px' }}>
            No cities found. Run the seed script or add one above.
          </div>
        ) : (
          cities.map((city, i) => (
            <div key={city.cityId} style={{
              display: 'grid',
              gridTemplateColumns: COL,
              padding: '0 20px',
              borderBottom: i < cities.length - 1 ? `1px solid ${COLORS.border}30` : 'none',
              alignItems: 'center',
            }}>
              <div style={cell}>
                <div style={{ fontWeight: '500' }}>{city.name}</div>
              </div>
              <div style={{ ...cell, color: COLORS.textSecondary, fontSize: '12px' }}>{city.state}</div>
              <div style={cell}>
                <span style={{
                  backgroundColor: city.enabled ? COLORS.success + '26' : COLORS.surfaceHover,
                  color: city.enabled ? COLORS.success : COLORS.textMuted,
                  borderRadius: '4px',
                  padding: '2px 8px',
                  fontSize: '11px',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  letterSpacing: '0.4px',
                }}>
                  {city.enabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
              <div style={{ ...cell, color: COLORS.textSecondary, fontSize: '12px' }}>
                {formatDate(city.enabledAt)}
              </div>
              <div style={cell}>
                <Toggle
                  checked={!!city.enabled}
                  onChange={() => handleToggle(city)}
                  disabled={!canWrite || toggling === city.cityId}
                />
              </div>
            </div>
          ))
        )}
      </div>
    </>
  );
}
